import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import {
  categories,
  whyReasons,
  safetyTips,
  blogArticles,
  popularCategories,
} from '@/mocks/siteData';

function CategoryDropdown() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  return (
    <div className="relative w-full max-w-md mx-auto">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <span className="text-sm font-medium">
          {selectedCategory || 'Select a category to explore...'}
        </span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-1 max-h-72 overflow-y-auto z-40">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleSelect(cat)}
              className="w-full flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer text-left"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} text-base`}></i>
              </div>
              {cat.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://vigorbuyspread.bond";

  return (
    <div className="min-h-screen bg-background-50 font-body">
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          "name": "Vigorbuy Spreadsheet Hub",
          "url": siteUrl,
          "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg",
          "description": "The most complete Vigorbuy Spreadsheet resource platform helping users discover products, compare categories, understand safety, and make smarter buying decisions.",
          "sameAs": [
            "https://kaspreadsheet.com"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": ["English"]
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "Vigorbuy Spreadsheet Hub",
          "description": "Discover the most complete Vigorbuy Spreadsheet resource platform. Explore product categories, read expert buying guides, compare collections, and make smarter shopping decisions.",
          "inLanguage": "en",
          "publisher": {
            "@id": `${siteUrl}/#organization`
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://kaspreadsheet.com/search-{search_term}/name/Shoes.html"
            },
            "query-input": "required name=search_term"
          }
        })}
      </script>
      <Navbar />

      <main>
      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20minimalist%20workspace%20with%20organized%20product%20cards%20and%20clean%20white%20desk%2C%20warm%20cream%20and%20beige%20background%20with%20gentle%20natural%20light%2C%20subtle%20plant%20accents%2C%20editorial%20lifestyle%20photography%2C%20airy%20and%20fresh%20aesthetic%2C%20no%20clutter%2C%20soft%20shadows%2C%20elegant%20composition&width=1600&height=900&seq=hero-bg-2026-v3&orientation=landscape"
            alt="Vigorbuy Spreadsheet organized product research workspace"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1 text-center lg:text-left w-full">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-background-50/90 rounded-full mb-6">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-500"></div>
                  <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">Updated August 2026</span>
                </div>

                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6">
                  Vigorbuy Spreadsheet &mdash; The Ultimate Product Research &amp; Shopping Guide
                </h1>

                <p className="text-base md:text-lg text-background-50/85 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                  Discover the latest Vigorbuy Spreadsheet collections, product categories, buying guides, quality checking tips, and expert recommendations in one trusted resource.
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap text-center cursor-pointer"
                  >
                    Vigorbuy Spreadsheet
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 bg-background-50/90 text-foreground-900 font-semibold text-sm rounded-full hover:bg-background-50 transition-all whitespace-nowrap text-center cursor-pointer"
                  >
                    Buy It Now
                  </a>
                </div>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 justify-center lg:justify-start mt-8">
                  {['Updated Resource Database', 'Beginner Friendly Guide', 'Product Research Hub', 'Safety Information Center'].map((badge) => (
                    <div key={badge} className="flex items-center gap-1.5 text-xs text-background-50/80 whitespace-nowrap">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-accent-400 text-sm"></i>
                      </div>
                      {badge}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 hidden lg:flex justify-center">
                <div className="relative w-80 h-80 md:w-96 md:h-96 rounded-2xl overflow-hidden bg-background-50/10 backdrop-blur-sm border border-background-50/10 flex items-center justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20product%20catalog%20dashboard%20layout%20with%20organized%20category%20cards%20and%20clean%20interface%2C%20warm%20cream%20background%2C%20soft%20studio%20lighting%2C%20modern%20UI%20mockup%20style%2C%20sophisticated%20editorial%20aesthetic%2C%20harmonious%20warm%20tone%20composition&width=800&height=800&seq=hero-dashboard-v3&orientation=squarish"
                    alt="Vigorbuy Spreadsheet dashboard preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Categories - Dropdown */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            Explore Vigorbuy Spreadsheet Categories
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-xl mx-auto">
            Choose a category below to browse organized product collections on the Vigorbuy Spreadsheet platform. Each category links directly to curated product listings.
          </p>
          <CategoryDropdown />
        </div>
      </section>

      {/* What Is Vigorbuy Spreadsheet */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              What Is Vigorbuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              Vigorbuy Spreadsheet is a structured product discovery system that helps shoppers organize, research, compare, and understand products more efficiently. Instead of searching through thousands of scattered pages, users can explore organized collections, categories, and recommendations in one streamlined platform.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="max-w-3xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-primary-500">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-background-50 whitespace-nowrap">Feature</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-background-50 whitespace-nowrap">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-close-line text-sm"></i>
                    </div>
                    Traditional Searching
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-background-50 whitespace-nowrap">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    Vigorbuy Spreadsheet
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200/70 bg-background-50">
                  <td className="px-6 py-4 text-sm font-medium text-foreground-800">Product Discovery</td>
                  <td className="px-6 py-4 text-sm text-foreground-500">Slow and fragmented</td>
                  <td className="px-6 py-4 text-sm text-accent-600 font-medium">Fast and structured</td>
                </tr>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <td className="px-6 py-4 text-sm font-medium text-foreground-800">Organization</td>
                  <td className="px-6 py-4 text-sm text-foreground-500">Poor and inconsistent</td>
                  <td className="px-6 py-4 text-sm text-accent-600 font-medium">Excellent organization</td>
                </tr>
                <tr className="border-b border-background-200/70 bg-background-50">
                  <td className="px-6 py-4 text-sm font-medium text-foreground-800">Category Browsing</td>
                  <td className="px-6 py-4 text-sm text-foreground-500">Difficult to navigate</td>
                  <td className="px-6 py-4 text-sm text-accent-600 font-medium">Easy and intuitive</td>
                </tr>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <td className="px-6 py-4 text-sm font-medium text-foreground-800">Research Process</td>
                  <td className="px-6 py-4 text-sm text-foreground-500">Time consuming</td>
                  <td className="px-6 py-4 text-sm text-accent-600 font-medium">Efficient and streamlined</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-6 py-4 text-sm font-medium text-foreground-800">Beginner Friendly</td>
                  <td className="px-6 py-4 text-sm text-foreground-500">Limited support</td>
                  <td className="px-6 py-4 text-sm text-accent-600 font-medium">Excellent for beginners</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Millions Prefer Vigorbuy Spreadsheet */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              Why Millions Of Users Prefer Vigorbuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              The Vigorbuy Spreadsheet platform has become the go-to resource for smart shoppers worldwide. Here is why it stands out from traditional product research methods.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyReasons.map((reason) => (
              <div key={reason.title} className="bg-background-100 rounded-xl p-6 md:p-7 hover:bg-background-200/70 transition-colors group cursor-default">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-5 group-hover:bg-accent-200 transition-colors">
                  <i className={`${reason.icon} text-accent-600 text-xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{reason.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Categories with Images */}
      <section id="categories" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              Explore Popular Vigorbuy Spreadsheet Categories
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              Browse through the most popular Vigorbuy Spreadsheet categories. Each section connects you to carefully curated product collections for a better shopping experience.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularCategories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
                <div className="p-5 flex items-center justify-between">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors">{cat.name}</h3>
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-500 transition-colors">
                    <i className="ri-arrow-right-line text-primary-500 group-hover:text-background-50 text-sm transition-colors"></i>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* All Category Buttons with Images */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              Browse All Vigorbuy Spreadsheet Categories
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Click any category below to jump directly to the corresponding product collection on the Vigorbuy Spreadsheet platform.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex items-center gap-4 p-4 bg-background-100 rounded-xl border border-background-200/70 hover:border-primary-200 hover:bg-background-200/50 transition-all cursor-pointer"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-400"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                      {cat.name}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-500">Browse collection →</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent-100 rounded-full mb-6">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-accent-600 text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-accent-700 uppercase tracking-wide whitespace-nowrap">Safety Center</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                Is Vigorbuy Spreadsheet Safe?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                Safety is the most important part of online shopping research. When using the Vigorbuy Spreadsheet platform, we recommend following these essential safety practices to protect your personal information and ensure a secure shopping experience.
              </p>

              <div className="space-y-3 mb-8">
                {safetyTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-sm text-foreground-700">{tip}</span>
                  </div>
                ))}
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                <p className="text-xs text-foreground-500 leading-relaxed">
                  <strong className="text-foreground-700">Disclaimer:</strong> Vigorbuyspread.bond is an independent informational resource. We provide guides, category navigation, and educational content about the Vigorbuy Spreadsheet platform. Users should verify all external websites before sharing personal information or making purchases. We are not responsible for transactions made on third-party websites.
                </p>
              </div>
            </div>

            <div className="flex-1 w-full">
              <div className="relative rounded-2xl overflow-hidden h-80 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20clean%20security%20and%20trust%20concept%20with%20shield%20icon%20on%20minimal%20desk%20setup%2C%20warm%20cream%20background%20with%20soft%20natural%20lighting%2C%20professional%20editorial%20photography%2C%20calm%20and%20trustworthy%20aesthetic%2C%20gentle%20shadows%2C%20elegant%20composition&width=800&height=600&seq=safety-img-v3&orientation=landscape"
                  alt="Vigorbuy Spreadsheet safety and security guide"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview - 10 Articles */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              Latest Vigorbuy Spreadsheet Guides &amp; Articles
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Read our comprehensive guides and articles to get the most out of your Vigorbuy Spreadsheet experience. Expert tips, tutorials, and comparisons — all in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blogArticles.map((article) => (
              <Link
                key={`${article.slug}-${article.title}`}
                to={`/blog/${article.slug}`}
                className="group flex flex-col sm:flex-row bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="relative w-full sm:w-48 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors leading-snug mb-2">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                  </div>
                  <div className="flex items-center gap-1.5 mt-3 text-primary-500 text-xs font-medium whitespace-nowrap">
                    Read Article
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/blog/vigorbuy-spreadsheet-complete-guide"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 border border-background-200/70 rounded-full text-sm font-medium text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-all whitespace-nowrap cursor-pointer"
            >
              View All Guides
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-6 py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20desk%20with%20organized%20product%20catalog%20layout%2C%20soft%20cream%20and%20beige%20tones%2C%20gentle%20natural%20lighting%20from%20window%2C%20modern%20clean%20workspace%20aesthetic%2C%20airy%20atmosphere%2C%20editorial%20lifestyle%20photography%2C%20no%20clutter%2C%20elegant%20composition&width=1600&height=700&seq=cta-bg-v3&orientation=landscape"
            alt="Vigorbuy Spreadsheet CTA background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/20 to-black/35"></div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            Ready To Explore Vigorbuy Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-lg mx-auto leading-relaxed">
            Discover organized product collections, compare categories, and make smarter shopping decisions with the complete Vigorbuy Spreadsheet platform.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap text-center cursor-pointer"
            >
              Visit Main Website
            </a>
            <Link
              to="/blog/vigorbuy-spreadsheet-complete-guide"
              className="w-full sm:w-auto px-8 py-3.5 bg-background-50/90 text-foreground-900 font-semibold text-sm rounded-full hover:bg-background-50 transition-all whitespace-nowrap text-center cursor-pointer"
            >
              Read Complete Guide
            </Link>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}